/**
 * API Proxy utility
 *
 * When VITE_RAG_DIRECT_FASTAPI=true, bypasses Supabase Edge Functions and calls
 * FastAPI directly via reverse proxy (nginx). This is safe when the proxy handles
 * API key injection server-side.
 */
import { supabase } from '@/integrations/supabase/client';

const FASTAPI_DIRECT_ENABLED =
  (import.meta.env.VITE_RAG_DIRECT_FASTAPI ?? 'false').toLowerCase() === 'true';

interface ApiProxyOptions {
  endpoint: string;
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  data?: unknown;
  params?: Record<string, string>;
}

interface ApiProxyResult<T = unknown> {
  data: T | null;
  error: Error | null;
}

async function getAccessToken(refresh = false): Promise<string | undefined> {
  const sessionResult = refresh
    ? await supabase.auth.refreshSession()
    : await supabase.auth.getSession();

  return sessionResult.data.session?.access_token;
}

export async function invokeApiProxy<T = unknown>(
  options: ApiProxyOptions
): Promise<ApiProxyResult<T>> {
  const { endpoint, method = 'POST', data, params } = options;
  const accessToken = await getAccessToken();

  if (FASTAPI_DIRECT_ENABLED) {
    // Direct call via reverse proxy (nginx)
    try {
      let url = endpoint;
      if (params) {
        const queryString = new URLSearchParams(params).toString();
        url += `?${queryString}`;
      }

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      };

      // Add Authorization header if we have a token
      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
      }

      const fetchOptions: RequestInit = {
        method,
        headers,
      };

      if (method !== 'GET' && data !== undefined) {
        fetchOptions.body = JSON.stringify(data);
      }

      let response = await fetch(url, fetchOptions);
      if (response.status === 401 && accessToken) {
        const refreshedToken = await getAccessToken(true);
        if (refreshedToken && refreshedToken !== accessToken) {
          response = await fetch(url, {
            ...fetchOptions,
            headers: {
              ...headers,
              Authorization: `Bearer ${refreshedToken}`,
            },
          });
        }
      }

      if (!response.ok) {
        const errorText = await response.text();
        return {
          data: null,
          error: new Error(`API error ${response.status}: ${errorText}`),
        };
      }

      const responseData = await response.json();
      return { data: responseData as T, error: null };
    } catch (err) {
      return {
        data: null,
        error: err instanceof Error ? err : new Error(String(err)),
      };
    }
  } else {
    // Call via Supabase Edge Function
    const invoke = (token?: string) => supabase.functions.invoke('api-proxy', {
      headers: token
        ? {
            Authorization: `Bearer ${token}`,
          }
        : undefined,
      body: {
        endpoint,
        method,
        data,
        params,
      },
    });

    let { data: responseData, error } = await invoke(accessToken);
    if (error && accessToken) {
      const refreshedToken = await getAccessToken(true);
      if (refreshedToken && refreshedToken !== accessToken) {
        ({ data: responseData, error } = await invoke(refreshedToken));
      }
    }

    return {
      data: responseData as T | null,
      error: error ? new Error(error.message) : null,
    };
  }
}

export { FASTAPI_DIRECT_ENABLED };
